import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { useAppContext } from "../../context";
import { ExternalLink } from "lucide-react";

interface ReferralBannerData {
  enabled: boolean;
  url?: string;
  title?: string;
  buttonText?: string;
}

export function Footer() {
  const { selectedMachine } = useAppContext();

  const { data: bannerData } = useQuery({
    queryKey: ["referral-banner"],
    queryFn: async () => {
      const response = await api.settings.referral.get();
      if (response.data && "enabled" in response.data) {
        return response.data as ReferralBannerData;
      }
      return { enabled: false };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const handleClick = async () => {
    if (!bannerData?.url) return;

    // Track the click
    try {
      await api.settings.referral.click.post({
        machineCode: selectedMachine ?? undefined,
      });
    } catch {
      // Ignore tracking errors
    }

    // Open the URL in a new tab
    window.open(bannerData.url, "_blank", "noopener,noreferrer");
  };

  return (
    <footer className="border-t border-border bg-card/30 mt-auto">
      <div className="container py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Left side - Branding */}
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Gap Tracker</span>{" "}
            &mdash; Gacha Statistics
          </div>

          {/* Center - Referral link if enabled */}
          {bannerData?.enabled && bannerData?.url && (
            <button
              onClick={handleClick}
              className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors">
              <span>{bannerData.buttonText || "Play on CollectorCrypt"}</span>
              <ExternalLink className="w-3 h-3" />
            </button>
          )}

          {/* Right side - Credits */}
          <div className="text-sm text-muted-foreground">
            Made by{" "}
            <a
              href="https://knutegil.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:text-primary transition-colors">
              knutegil.dev
            </a>{" "}
            with data from{" "}
            <a
              href="https://collectorcrypt.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:text-primary transition-colors">
              Collector Crypt
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
